import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useObraStore } from '@stores/obraStore.js'
import { useModeStore } from '@stores/modeStore.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { Select } from '@components/primitives/Select.js'
import { Modal } from '@components/primitives/Modal.js'
import { FASES_CONSTRUTIVAS, PADROES_CONSTRUCAO, ROUTES } from '@utils/constants.js'
import { importarBackup } from '@services/backup.js'
import logoMark from '@assets/logo-mark.svg'

function IconGuide() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' aria-hidden>
      <path d='M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2' />
      <rect x='9' y='3' width='6' height='4' rx='1' />
      <path d='M9 12h6M9 16h6' />
    </svg>
  )
}

function IconFinance() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' aria-hidden>
      <path d='M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />
    </svg>
  )
}

function IconVault() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' aria-hidden>
      <path d='M3 7v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7' />
      <path d='M3 7l9-4 9 4M12 11v6' />
    </svg>
  )
}

function IconOffline() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' aria-hidden>
      <rect x='3' y='11' width='18' height='11' rx='2' />
      <path d='M7 11V7a5 5 0 0 1 10 0v4' />
    </svg>
  )
}

const TIPO_CONTRATACAO = [
  { value: 'empreitada-total', label: 'Empreitada Total' },
  { value: 'empreitada-parcial', label: 'Empreitada Parcial' },
  { value: 'administracao', label: 'Por Administração' },
  { value: 'ainda-nao-sei', label: 'Ainda não sei' },
]

const FASE_CONSTRUTIVA_OPTIONS = [
  { value: '', label: 'Ainda não comecei' },
  ...FASES_CONSTRUTIVAS.map((f) => ({ value: f.id, label: f.label })),
]

interface FormData {
  nome: string
  dataInicio: string
  areaTotal: string
  padraoConstrucao: string
  orcamentoTotal: string
  tipoContratacao: string
  faseConstrutiva: string
}

type Phase = 'welcome' | 'forms'

export function OnboardingPage() {
  const navigate = useNavigate()
  const { save, load } = useObraStore()
  const setMode = useModeStore((s) => s.setMode)

  const [phase, setPhase] = useState<Phase>('welcome')
  const [showStorageModal, setShowStorageModal] = useState(false)
  const [understoodStorage, setUnderstoodStorage] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Partial<FormData>>({})

  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState<FormData>({
    nome: '',
    dataInicio: new Date().toISOString().slice(0, 10),
    areaTotal: '',
    padraoConstrucao: '',
    orcamentoTotal: '',
    tipoContratacao: '',
    faseConstrutiva: '',
  })

  const setField =
    (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm((f) => ({ ...f, [field]: e.target.value }))
      setErrors((er) => ({ ...er, [field]: '' }))
    }

  function handleComecar() {
    setShowStorageModal(true)
  }

  function handleAcceptStorage() {
    setShowStorageModal(false)
    setUnderstoodStorage(false)
    setPhase('forms')
  }

  function validateForm(): boolean {
    const errs: Partial<FormData> = {}
    if (!form.nome.trim()) errs.nome = 'Informe o nome da obra'
    if (!form.areaTotal) errs.areaTotal = 'Informe a área total'
    if (!form.padraoConstrucao) errs.padraoConstrucao = 'Selecione o padrão'
    if (!form.orcamentoTotal) errs.orcamentoTotal = 'Informe o orçamento'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function finish() {
    if (!validateForm()) return
    setSaving(true)
    try {
      await save({
        nome: form.nome.trim(),
        dataInicio: form.dataInicio,
        areaTotal: Number(form.areaTotal) || 0,
        padraoConstrucao: form.padraoConstrucao,
        regiao: '',
        orcamentoTotal: Number(form.orcamentoTotal) || 0,
        faseAtual: '',
        tipoContratacao: form.tipoContratacao,
        ...(form.faseConstrutiva ? { faseConstrutiva: form.faseConstrutiva } : {}),
      })
      setMode('guia')
      navigate(ROUTES.HOME, { replace: true })
    } finally {
      setSaving(false)
    }
  }

  function handleSelecionarImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      setImportFile(file)
      setShowImportModal(true)
    }
  }

  async function handleConfirmarImport() {
    if (!importFile) return
    setImporting(true)
    setImportError(null)
    try {
      await importarBackup(importFile)
      await load()
      setMode('guia')
      setShowImportModal(false)
      setImportFile(null)
      navigate(ROUTES.HOME, { replace: true })
    } catch {
      setImportError('Erro ao importar backup. Verifique o arquivo e tente novamente.')
    } finally {
      setImporting(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // ── Tela de boas-vindas (RFC-01.2) ─────────────────────────
  if (phase === 'welcome') {
    return (
      <div className='cf3-onboarding cf3-page cf3-page--guia cf3-animate-fade-in' role='main'>
        <div className='cf3-welcome'>
          <div className='cf3-welcome__hero'>
            <img
              src={logoMark}
              className='cf3-welcome__logo-mark'
              width={80}
              height={80}
              alt=''
              aria-hidden
            />
            <h1 className='cf3-welcome__title'>Minha Obra</h1>
            <p className='cf3-welcome__tagline'>Um guia de construção e apoio</p>
            <p className='cf3-welcome__tagline'>Do terreno à chave na mão</p>
          </div>

          <div className='cf3-welcome__tiles'>
            <div className='cf3-welcome__tile'>
              <span className='cf3-welcome__tile-icon'>
                <IconGuide />
              </span>
              <p className='cf3-welcome__tile-title'>{GUIA_LABELS.guiaEtapaAEtapa}</p>
              <p className='cf3-welcome__tile-sub'>Decisões na ordem certa</p>
            </div>
            <div className='cf3-welcome__tile'>
              <span className='cf3-welcome__tile-icon'>
                <IconFinance />
              </span>
              <p className='cf3-welcome__tile-title'>Controle financeiro</p>
              <p className='cf3-welcome__tile-sub'>Registre seus gastos</p>
            </div>
            <div className='cf3-welcome__tile'>
              <span className='cf3-welcome__tile-icon'>
                <IconVault />
              </span>
              <p className='cf3-welcome__tile-title'>Controle de arquivos</p>
              <p className='cf3-welcome__tile-sub'>Guarde seus documentos</p>
            </div>
            <div className='cf3-welcome__tile'>
              <span className='cf3-welcome__tile-icon'>
                <IconOffline />
              </span>
              <p className='cf3-welcome__tile-title'>Sem internet</p>
              <p className='cf3-welcome__tile-sub'>Funciona no aparelho, sem login</p>
            </div>
          </div>

          <Button onClick={handleComecar} size='lg' className='cf3-welcome__cta'>
            Começar minha obra
          </Button>

          <Button
            variant='secondary'
            onClick={() => fileInputRef.current?.click()}
            className='cf3-welcome__import'
          >
            Importar backup
          </Button>
          <input
            ref={fileInputRef}
            type='file'
            accept='.zip,application/zip'
            className='cf3-config-file-input'
            onChange={handleSelecionarImport}
          />
        </div>

        {/* Modal de aviso de armazenamento local (RFC-01.3) */}
        <Modal
          open={showStorageModal}
          title='Sobre seus dados'
          closable={false}
          size='sm'
          actions={
            <Button onClick={handleAcceptStorage} disabled={!understoodStorage}>
              Continuar
            </Button>
          }
        >
          <div className='cf-storage-modal'>
            <p>
              Seus dados ficam salvos <strong>neste aparelho</strong>. Se você limpar os dados do
              navegador ou desinstalar o app, todas as informações serão perdidas.
            </p>
            <p>
              Por isso, <strong>exporte backups regularmente</strong> na tela de Configurações. Os
              dados não são copiados automaticamente para outro aparelho.
            </p>
            <label className='cf-storage-modal__check'>
              <input
                type='checkbox'
                checked={understoodStorage}
                onChange={(e) => setUnderstoodStorage(e.target.checked)}
                aria-label='Confirmar que entendi onde os dados ficam guardados'
              />
              <span>Entendi. Tudo fica neste aparelho, de forma privada.</span>
            </label>
          </div>
        </Modal>

        {/* Modal de confirmação de importação de backup */}
        <Modal
          open={showImportModal}
          title='Importar backup'
          closable={!importing}
          onClose={() => !importing && setShowImportModal(false)}
          size='sm'
          actions={
            <>
              <Button
                variant='secondary'
                onClick={() => setShowImportModal(false)}
                disabled={importing}
              >
                Cancelar
              </Button>
              <Button onClick={handleConfirmarImport} disabled={importing}>
                {importing ? 'Importando...' : 'Importar'}
              </Button>
            </>
          }
        >
          <div className='cf-storage-modal'>
            <p>
              Importar o backup <strong>{importFile?.name}</strong>?
            </p>
            <p>Isso substituirá todos os dados atuais do app pelos dados do arquivo.</p>
            {importError && (
              <p className='cf3-error-text' role='alert'>
                {importError}
              </p>
            )}
          </div>
        </Modal>
      </div>
    )
  }

  // ── Formulário único (RFC-01.4 a 01.8) ─────────────────────
  return (
    <div className='cf3-onboarding cf3-page cf3-page--guia cf3-animate-fade-in' role='main'>
      <div className='cf3-onboarding__form'>
        <div className='cf3-onboarding__step cf3-animate-fade-in'>
          <h2>Configure sua obra</h2>
          <p>Preencha os dados abaixo para personalizar sua experiência.</p>

          <Input
            name='nome'
            label='Nome da Obra'
            placeholder='Ex: Casa da Família Silva'
            required
            value={form.nome}
            onChange={setField('nome')}
            error={errors.nome ?? ''}
          />
          <Input
            name='dataInicio'
            label='Data de Início'
            type='date'
            value={form.dataInicio}
            onChange={setField('dataInicio')}
          />
          <Input
            name='areaTotal'
            label='Área Total (m²)'
            placeholder='Ex: 150'
            type='number'
            suffix='m²'
            required
            value={form.areaTotal}
            onChange={setField('areaTotal')}
            error={errors.areaTotal ?? ''}
          />
          <Select
            name='padraoConstrucao'
            label='Padrão de Construção'
            placeholder='Selecione...'
            required
            value={form.padraoConstrucao}
            onChange={setField('padraoConstrucao')}
            options={PADROES_CONSTRUCAO}
            error={errors.padraoConstrucao ?? ''}
          />
          <MoneyInput
            label='Orçamento Total'
            required
            value={form.orcamentoTotal ? Number(form.orcamentoTotal) : 0}
            onChange={(v) => {
              setForm((f) => ({ ...f, orcamentoTotal: v > 0 ? String(v) : '' }))
              setErrors((er) => ({ ...er, orcamentoTotal: '' }))
            }}
            error={errors.orcamentoTotal ?? ''}
          />
          <Select
            name='tipoContratacao'
            label='Tipo de Contratação'
            placeholder='Selecione...'
            value={form.tipoContratacao}
            onChange={setField('tipoContratacao')}
            options={TIPO_CONTRATACAO}
          />
          <Select
            name='faseConstrutiva'
            label='Fase construtiva'
            hint='Opcional — personaliza o painel da obra'
            value={form.faseConstrutiva}
            onChange={setField('faseConstrutiva')}
            options={FASE_CONSTRUTIVA_OPTIONS}
          />
        </div>

        <div>
          <Button onClick={finish} loading={saving} className='cf3-onboarding__next'>
            Começar minha obra
          </Button>
        </div>
      </div>
    </div>
  )
}
